<template>
  <div>
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between mb-4">
          <p class="text-2xl mb-0">
            Automações de Serviço
          </p>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card flat>
          <v-card-text class="pa-3">
            <v-row>
              <v-col
                cols="12"
                sm="12"
                md="4"
              >
                <v-text-field
                  v-model="filterName"
                  :append-icon="icons.mdiMagnify"
                  label="Nome"
                  hide-details
                  outlined
                  dense
                  @click:append="applyFilter"
                  @keypress.enter="applyFilter"
                />
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider />
          <v-card-actions class="pa-3">
            <v-btn
              color="primary"
              class="pl-4"
              @click="applyFilter"
            >
              Pesquisar
              <v-icon
                right
                dark
              >
                {{ icons.mdiMagnify }}
              </v-icon>
            </v-btn>
            <v-btn
              color="error"
              outlined
              @click="clearFilter"
            >
              Limpar filtro
            </v-btn>

            <v-spacer />

            <v-btn
              color="primary"
              class="pl-4"
              outlined
              @click="openFormToCreate"
            >
              Adicionar nova automação
              <v-icon
                right
                dark
              >
                {{ icons.mdiTablePlus }}
              </v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="filteredAutomations"
          :items-per-page="10"
          item-key="id"
          disable-sort
          dense
        >
          <!-- eslint-disable vue/valid-v-for -->
          <template
            v-for="col in headersWithFilter"
            v-slot:[`header.${col}`]="{ header }"
          >
            {{ header.text }}

            <v-btn
              color="white"
              icon
              @click="dialogFilter = true"
            >
              <v-icon small>
                {{ icons.mdiFilterMenu }}
              </v-icon>
            </v-btn>
          </template>
          <!-- eslint-disable vue/valid-v-for -->

          <template #[`item.action`]="{ item }">
            <v-btn
              color="info"
              title="Editar"
              icon
              @click="openFormToEdit(item)"
            >
              <v-icon>{{ icons.mdiFileEdit }}</v-icon>
            </v-btn>
            <v-btn
              color="error"
              title="Excluir"
              icon
              @click="confirmDelete(item)"
            >
              <v-icon>{{ icons.mdiDelete }}</v-icon>
            </v-btn>
          </template>

          <template #[`item.name`]="{ item }">
            <div
              class="text-truncate"
              style="max-width: 200px;"
            >
              {{ item.name }}
            </div>
          </template>

          <template #[`item.status`]="{ item }">
            <v-chip
              :color="resolveStatus(item.status).color"
              small
              label
            >
              <span class="white--text">
                {{ resolveStatus(item.status).label }}
              </span>
            </v-chip>
          </template>

          <template #[`item.channelType`]="{ item }">
            <v-chip
              small
              :color="resolveChannelType(item.channelType).color"
              label
            >
              <span class="white--text">
                {{ resolveChannelType(item.channelType).label }}
              </span>
            </v-chip>
          </template>

          <template #[`item.listName`]="{ item }">
            <span>{{ item.listName || '-' }}</span>
          </template>

          <template #[`item.createdAt`]="{ item }">
            <div class="d-flex flex-column my-1">
              <span>
                <v-icon size="18">
                  {{ icons.mdiCalendar }}
                </v-icon>
                {{ $day(item.createdAt).format('DD/MM/YYYY') }}
              </span>
              <span>
                <v-icon size="18">
                  {{ icons.mdiClock }}
                </v-icon>
                {{ $day(item.createdAt).format('HH:mm') }}
              </span>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <filter-service-automations
      :status="dialogFilter"
      @filter="onAdvancedFilter"
      @close="dialogFilter = false"
    />

    <v-dialog
      v-model="dialogCreateAutomation"
      max-width="1000"
      persistent
    >
      <v-card>
        <v-card-title class="text-h5 grey lighten-2">
          {{ isEditMode ? 'Editar automação' : 'Nova automação' }}
          <v-chip
            v-if="isEditMode && createAutomationForm.channelType"
            small
            :color="createAutomationForm.channelType === 'rcs' ? 'success' : 'info'"
            class="ml-3"
            label
          >
            {{ createAutomationForm.channelType === 'rcs' ? 'RCS' : 'SMS' }}
          </v-chip>
          <v-spacer />
        </v-card-title>

        <v-card-text class="pt-6">
          <v-row>
            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <v-text-field
                v-model="createAutomationForm.name"
                label="Nome"
                hide-details
                outlined
                dense
                clearable
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <v-select
                v-model="createAutomationForm.channelType"
                :items="channelTypeItems"
                label="Tipo da automação"
                item-text="label"
                item-value="value"
                hide-details
                outlined
                dense
                :disabled="isEditMode"
                :hint="isEditMode ? 'O tipo da automação não pode ser alterado' : 'Escolha SMS ou RCS antes de salvar'"
                persistent-hint
                @change="onAutomationChannelTypeChanged"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <v-select
                v-model="createAutomationForm.listId"
                :items="listItems"
                label="Lista"
                item-text="label"
                item-value="value"
                hide-details
                outlined
                dense
                clearable
                no-data-text="Nenhuma lista ativa para este tipo"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <v-switch
                v-model="createAutomationForm.isActive"
                inset
                hide-details
                label="Ativar?"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <v-select
                v-model="createAutomationForm.sendMode"
                :items="sendModeItems"
                label="Modo de envio"
                item-text="label"
                item-value="value"
                hide-details
                outlined
                dense
              />
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="createAutomationForm.description"
                label="Descrição"
                hide-details
                outlined
                auto-grow
              />
            </v-col>
          </v-row>

          <!-- Sequência de ações - apenas no modo edição -->
          <v-row v-if="isEditMode">
            <v-col cols="12">
              <div class="d-flex align-center justify-space-between mb-3">
                <span class="text-subtitle-1 font-weight-medium">
                  SEQUÊNCIA
                </span>

                <div class="d-flex flex-wrap">
                  <v-btn
                    v-if="createAutomationForm.channelType === 'sms'"
                    color="info"
                    small
                    class="mb-1"
                    @click="openSmsActionForm()"
                  >
                    Adicionar ação SMS
                    <v-icon
                      right
                      small
                    >
                      {{ icons.mdiPlus }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    v-else-if="createAutomationForm.channelType === 'rcs'"
                    color="success"
                    small
                    class="mb-1"
                    @click="openRcsActionForm()"
                  >
                    Adicionar ação RCS
                    <v-icon
                      right
                      small
                    >
                      {{ icons.mdiPlus }}
                    </v-icon>
                  </v-btn>
                </div>
              </div>

              <v-card
                v-for="item in sequences"
                :key="item.id"
                class="mb-3"
                outlined
              >
                <v-card-text class="d-flex flex-column flex-md-row align-start align-md-center">
                  <div class="mr-4 mb-2 mb-md-0 d-flex align-center">
                    <v-switch
                      v-model="item.enabled"
                      inset
                      hide-details
                      class="mr-2"
                      @change="toggleActionEnabled(item)"
                    />
                  </div>

                  <div class="mr-4 mb-2 mb-md-0">
                    <v-chip
                      small
                      :color="getActionChipColor(item.typeSms)"
                      text-color="white"
                      class="mb-1"
                    >
                      {{ item.typeLabel }}
                    </v-chip>
                    <div class="text-caption text--secondary">
                      {{ item.delayLabel }}
                    </div>
                  </div>

                  <div class="flex-grow-1 mb-2 mb-md-0">
                    <div class="text-body-2">
                      {{ item.message }}
                    </div>
                  </div>

                  <div class="d-flex align-center">
                    <v-btn
                      icon
                      color="info"
                      class="mr-1"
                      @click="openActionForm(item)"
                    >
                      <v-icon small>
                        {{ icons.mdiFileEdit }}
                      </v-icon>
                    </v-btn>
                    <v-btn
                      icon
                      color="error"
                      @click="confirmDeleteAction(item)"
                    >
                      <v-icon small>
                        {{ icons.mdiDelete }}
                      </v-icon>
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="error"
            outlined
            @click="closeCreateAutomationDialog"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="info"
            :disabled="!createAutomationForm.name || !createAutomationForm.channelType"
            @click="submitCreateAutomation"
          >
            {{ isEditMode ? 'Atualizar' : 'Salvar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de ação SMS -->
    <v-dialog
      v-model="dialogAction"
      max-width="900"
      persistent
    >
      <v-card>
        <v-card-title class="text-h5 grey lighten-2">
          Configurar ação de SMS
          <v-chip
            small
            color="info"
            class="ml-3"
            label
          >
            SMS
          </v-chip>
          <v-spacer />
          <v-btn
            text
            x-small
            fab
            @click="dialogAction = false"
          >
            <v-icon color="error">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-row>
            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <v-text-field
                v-model="actionForm.name"
                label="Nome da Campanha"
                hide-details
                outlined
                dense
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <v-select
                v-model="actionForm.typeSms"
                :items="filteredTypes"
                outlined
                item-text="text"
                item-value="value"
                dense
                label="Tipo"
                hide-details
                @change="filterActionTemplates"
              >
                <template v-slot:item="{ item }">
                  <v-tooltip
                    right
                    open-on-hover
                    nudge-right="50"
                    max-width="320"
                    class="me-5"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        style="display: flex; justify-content: flex-end"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ icons.mdiInformationOutline }}
                      </v-icon>
                    </template>
                    <span>{{ item.description }}</span>
                  </v-tooltip>
                  <span class="mr-5">&nbsp; {{ item.text }}</span>
                </template>
              </v-select>
            </v-col>

            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <v-select
                v-model="actionForm.variableMode"
                :items="[
                  { text: 'Dinâmicas (padrão)', value: 'dynamic' },
                  // { text: 'Fixas (integração body fixo)', value: 'fixed' },
                ]"
                outlined
                dense
                item-text="text"
                item-value="value"
                label="Variáveis"
                hide-details
                @change="onVariableModeChanged"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <template v-if="actionForm.variableMode === 'fixed'">
                <v-btn
                  color="primary"
                  outlined
                  block
                  @click="openFixedTemplateDialog"
                >
                  Cadastrar template (variáveis fixas)
                </v-btn>
                <div
                  v-if="selectedActionTemplateName"
                  class="text-caption mt-2"
                >
                  Selecionado: <b>{{ selectedActionTemplateName }}</b>
                </div>
                <div
                  v-else
                  class="text-caption mt-2"
                >
                  Nenhum template selecionado.
                </div>
              </template>

              <v-select
                v-else
                v-model="actionForm.smsTemplateId"
                :items="actionTemplates"
                outlined
                dense
                item-text="name"
                item-value="id"
                label="Template"
                hide-details
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <v-row>
                <v-col
                  cols="6"
                  sm="6"
                >
                  <v-text-field
                    v-model.number="actionForm.delayAmount"
                    type="number"
                    min="0"
                    label="Disparar após"
                    hide-details
                    outlined
                    dense
                  />
                </v-col>
                <v-col
                  cols="6"
                  sm="6"
                >
                  <v-select
                    v-model="actionForm.delayUnit"
                    :items="[
                      { text: 'Minuto(s)', value: 'minutes' },
                      { text: 'Hora(s)', value: 'hours' },
                    ]"
                    item-text="text"
                    item-value="value"
                    label="Unidade"
                    hide-details
                    outlined
                    dense
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="error"
            outlined
            @click="dialogAction = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="info"
            :disabled="!actionForm.name || !actionForm.smsTemplateId || !actionForm.delayAmount"
            @click="submitAction"
          >
            {{ editingActionId ? 'Atualizar ação' : 'Salvar ação' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal de ação RCS -->
    <v-dialog
      v-model="dialogActionRcs"
      max-width="900"
      persistent
    >
      <v-card>
        <v-card-title class="text-h5 grey lighten-2">
          Configurar ação de RCS
          <v-chip
            small
            color="success"
            class="ml-3"
            label
          >
            RCS
          </v-chip>
          <v-spacer />
          <v-btn
            text
            x-small
            fab
            @click="dialogActionRcs = false"
          >
            <v-icon color="error">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-alert
            type="info"
            dense
            outlined
            class="mb-4"
          >
            Esta ação criará campanhas RCS enviadas pela integração Ótima.
          </v-alert>

          <v-row>
            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <v-text-field
                v-model="rcsActionForm.name"
                label="Nome da Campanha"
                hide-details
                outlined
                dense
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <v-select
                v-model="rcsActionForm.supplier"
                :items="rcsSuppliers"
                item-text="text"
                item-value="value"
                outlined
                dense
                label="Fornecedor RCS"
                hide-details
                @change="filterRcsActionTemplates"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <v-select
                v-model="rcsActionForm.smsTemplateId"
                :items="rcsActionTemplates"
                :disabled="!rcsActionForm.supplier"
                outlined
                dense
                item-text="name"
                item-value="id"
                label="Template RCS"
                hide-details
                no-data-text="Selecione um fornecedor"
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <v-row>
                <v-col
                  cols="6"
                  sm="6"
                >
                  <v-text-field
                    v-model.number="rcsActionForm.delayAmount"
                    type="number"
                    min="0"
                    label="Disparar após"
                    hide-details
                    outlined
                    dense
                  />
                </v-col>
                <v-col
                  cols="6"
                  sm="6"
                >
                  <v-select
                    v-model="rcsActionForm.delayUnit"
                    :items="[
                      { text: 'Minuto(s)', value: 'minutes' },
                      { text: 'Hora(s)', value: 'hours' },
                    ]"
                    item-text="text"
                    item-value="value"
                    label="Unidade"
                    hide-details
                    outlined
                    dense
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="error"
            outlined
            @click="dialogActionRcs = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="success"
            :disabled="!rcsActionForm.name || !rcsActionForm.supplier || !rcsActionForm.smsTemplateId || !rcsActionForm.delayAmount"
            @click="submitRcsAction"
          >
            {{ editingActionId ? 'Atualizar ação RCS' : 'Salvar ação RCS' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal: cadastrar template SMS (variáveis fixas) -->
    <v-dialog
      v-model="dialogCreateFixedTemplate"
      max-width="1100"
      persistent
    >
      <v-card>
        <v-card-title class="text-h5 grey lighten-2">
          Cadastrar template SMS (variáveis fixas)
          <v-spacer />
          <v-btn
            text
            x-small
            fab
            @click="closeFixedTemplateDialog"
          >
            <v-icon color="error">
              {{ icons.mdiClose }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-row>
            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <v-text-field
                v-model="fixedTemplateForm.name"
                label="Nome do Template"
                hide-details
                outlined
                dense
              />
            </v-col>

            <v-col
              cols="12"
              md="6"
              sm="12"
            >
              <v-text-field
                v-model="fixedTemplateNewVariable"
                label="Adicionar variável (ex: ORDER_ID)"
                hide-details
                outlined
                dense
                @keypress.enter="addFixedVariable"
              />
            </v-col>

            <v-col cols="12">
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="v in fixedVariablesAll"
                  :key="v"
                  class="ma-1"
                  color="info"
                  outlined
                  @click="insertFixedVariable(v)"
                >
                  <span class="white--text">{{ '{' + v + '}' }}</span>
                </v-chip>
              </div>
              <div class="text-caption mt-2">
                Clique em uma variável para inserir na mensagem. A variável <b>{NUMERO}</b> é reservada para o destinatário.
              </div>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="fixedTemplateForm.message"
                label="Mensagem"
                outlined
                dense
                auto-grow
                counter="160"
                hint="Use apenas as variáveis acima (entre chaves), ex: {ORDER_ID}"
                persistent-hint
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider />

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="error"
            outlined
            @click="closeFixedTemplateDialog"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="info"
            :disabled="!fixedTemplateForm.name || !fixedTemplateForm.message"
            @click="submitFixedTemplate"
          >
            Salvar template
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { ref, computed } from '@vue/composition-api'
import { mapState, mapActions } from 'vuex'
import {
  mdiCalendar,
  mdiClock,
  mdiFilterMenu,
  mdiFileEdit,
  mdiClose,
  mdiMagnify,
  mdiTablePlus,
  mdiDelete,
  mdiPlus,
  mdiInformationOutline,
} from '@mdi/js'
import {
  smsTypes,
  smsTemplateType,
  smsTemplate as smsTemplateEnum,
} from '@/utils/enum'

const RCS_SMS_TYPE = smsTypes.find(type => type.serviceId === 31)?.value ?? 1200

function buildRcsSuppliers() {
  let suppliers = []

  if (process.env.VUE_APP_EMPRESA_ID == 1 || process.env.VUE_APP_EMPRESA_ID == 8) {
    suppliers = [
      { text: 'RCS Ligdata Basic', value: { broker_code: 'rcs_ligdata_basic', customer_code: 'rcs_ligdata', type: 'basic' }, type: 'basic' },
      { text: 'RCS Ligdata Single', value: { broker_code: 'rcs_ligdata_single', customer_code: 'rcs_ligdata', type: 'single' }, type: 'single' },
      { text: 'RCS Ligdata Conversational', value: { broker_code: 'rcs_ligdata_conversational', customer_code: 'rcs_ligdata', type: 'conversational' }, type: 'conversational' },
      { text: 'RCS PIXBET Single', value: { broker_code: 'rcs_pixbet_single', customer_code: 'PIXBET', type: 'single' }, type: 'single' },
      { text: 'RCS Hands Digital Basic', value: { broker_code: 'HANDS DIGITAL', customer_code: 'PIXBET', type: 'basic' }, type: 'basic' },
      { text: 'RCS Hands Digital Single', value: { broker_code: 'HANDS DIGITAL', customer_code: 'PIXBET', type: 'single' }, type: 'single' },
    ]
  }

  if (process.env.VUE_APP_EMPRESA_ID == 2 || process.env.VUE_APP_EMPRESA_ID == 3 || process.env.VUE_APP_EMPRESA_ID == 4) {
    suppliers = [
      { text: 'RCS PIXBET Single', value: { broker_code: 'rcs_pixbet_single', customer_code: 'PIXBET', type: 'single' }, type: 'single' },
    ]
  }

  if (process.env.VUE_APP_EMPRESA_ID == 9) {
    suppliers = [
      { text: 'KING PANDA', value: { broker_code: 'KING PANDA', customer_code: 'rcs_ligdata', type: 'single' }, type: 'single' },
    ]
  }

  return suppliers
}

export default {
  name: 'ServiceAutomations',

  components: {
    FilterServiceAutomations: () => import('./filter.vue'),
  },

  setup() {
    const dialogFilter = ref(false)
    const filterName = ref(null)
    const dialogCreateAutomation = ref(false)
    const isEditMode = ref(false)
    const editingAutomationId = ref(null)
    const advancedFilter = ref({})
    const dialogAction = ref(false)
    const dialogActionRcs = ref(false)
    const editingActionId = ref(null)
    const dialogCreateFixedTemplate = ref(false)
    const fixedTemplateNewVariable = ref('')

    const createAutomationForm = ref({
      name: null,
      listId: null,
      channelType: 'sms',
      isActive: true,
      sendMode: 'new',
      trigger: null,
      description: null,
    })

    const sequences = ref([])

    const actionForm = ref({
      name: null,
      typeSms: null,
      smsTemplateId: null,
      variableMode: 'dynamic',
      delayAmount: null,
      delayUnit: 'minutes',
    })

    const rcsActionForm = ref({
      name: null,
      supplier: null,
      smsTemplateId: null,
      delayAmount: null,
      delayUnit: 'minutes',
    })

    const rcsSuppliers = buildRcsSuppliers()

    const fixedTemplateForm = ref({
      name: null,
      message: '',
    })

    const fixedVariablesPreset = ref([
      'NUMERO',
      'FIRST_NAME',
      'LAST_NAME',
      'EMAIL',
      'LINK',
    ])

    const fixedVariablesCustom = ref([])

    const allTemplates = ref([])
    const actionTemplates = ref([])
    const allRcsTemplates = ref([])
    const rcsActionTemplates = ref([])

    const fixedVariablesAll = computed(() => {
      const upper = v => String(v || '').trim().toUpperCase()
      const merged = [...(fixedVariablesPreset.value || []), ...(fixedVariablesCustom.value || [])]
        .map(upper)
        .filter(Boolean)
      return Array.from(new Set(merged))
    })

    const selectedActionTemplateName = computed(() => {
      const id = actionForm.value?.smsTemplateId
      if (!id) return null
      const t = (allTemplates.value || []).find(x => String(x.id) === String(id))
      return t?.name || `Template #${id}`
    })

    const headers = [
      { text: 'Ações', value: 'action', align: 'center' },
      { text: 'ID', value: 'id', align: 'center' },
      { text: 'Nome', value: 'name' },
      { text: 'Tipo', value: 'channelType' },
      { text: 'Status', value: 'status' },
      { text: 'Lista', value: 'listName' },
      { text: 'Data de Criação', value: 'createdAt' },
    ]

    const headersWithFilter = [
      'id',
      'name',
      'channelType',
      'status',
      'listName',
      'createdAt',
    ]

    const channelTypeItems = [
      { label: 'SMS', value: 'sms' },
      { label: 'RCS', value: 'rcs' },
    ]

    const statusItems = [
      { label: 'Ativo', value: 1 },
      { label: 'Inativo', value: 0 },
    ]

    const sendModeItems = [
      { label: 'Enviar apenas para novos leads', value: 'new' },
      { label: 'Enviar para leads atuais e novos leads', value: 'all' },
    ]

    const resolveStatus = status => {
      const statusMap = {
        1: { label: 'Ativo', color: 'success' },
        0: { label: 'Inativo', color: 'error' },
      }

      return statusMap[status] || { label: 'Não definido', color: 'grey' }
    }

    const resolveChannelType = channelType => {
      const typeMap = {
        sms: { label: 'SMS', color: 'info' },
        rcs: { label: 'RCS', color: 'success' },
      }

      return typeMap[channelType] || { label: 'SMS', color: 'info' }
    }

    return {
      dialogFilter,
      filterName,
      dialogCreateAutomation,
      isEditMode,
      editingAutomationId,
      createAutomationForm,
      sequences,
      dialogAction,
      dialogActionRcs,
      editingActionId,
      actionForm,
      rcsActionForm,
      rcsSuppliers,
      allTemplates,
      actionTemplates,
      allRcsTemplates,
      rcsActionTemplates,
      selectedActionTemplateName,
      rcsSmsType: RCS_SMS_TYPE,
      headers,
      headersWithFilter,
      statusItems,
      sendModeItems,
      channelTypeItems,
      resolveStatus,
      resolveChannelType,
      advancedFilter,
      dialogCreateFixedTemplate,
      fixedTemplateForm,
      fixedTemplateNewVariable,
      fixedVariablesAll,

      icons: {
        mdiCalendar,
        mdiClock,
        mdiFilterMenu,
        mdiFileEdit,
        mdiClose,
        mdiMagnify,
        mdiTablePlus,
        mdiDelete,
        mdiInformationOutline,
        mdiPlus,
      },
      types: smsTypes,
    }
  },

  computed: {
    ...mapState('serviceListAutomation', {
      lists: 'rows',
    }),
    ...mapState('serviceAutomations', {
      automations: 'rows',
    }),

    filteredTypes() {
      const userId = localStorage.getItem('id')

      if (process.env.VUE_APP_EMPRESA_ID == 1 && (userId === 941 || userId === '941')) {
        return this.types.filter(type => type.serviceId === 6 || type.serviceId === 7)
      }

      if (process.env.VUE_APP_EMPRESA_ID == 1 && (userId === '937' || userId === 937 || userId === '938' || userId === 938 || userId === '939' || userId === 939 || userId === '942' || userId === 942 || userId === '940' || userId === 940 || userId === '948' || userId === 948 || userId === '949' || userId === 949 || userId === '950' || userId === 950 || userId === '951' || userId === 951)) {
        return this.types.filter(type => type.serviceId === 30)
      }

      if (process.env.VUE_APP_EMPRESA_ID == 1 && (userId === '936' || userId === 936 || userId === '947' || userId === 947 || userId === '957' || userId === 957)) {
        return this.types.filter(type => type.serviceId == 6 || type.serviceId == 7)
      }

      if (process.env.VUE_APP_EMPRESA_ID == 1 && (userId === '943' || userId === 943)) {
        return this.types.filter(type => type.serviceId === 8)
      }

      if (process.env.VUE_APP_EMPRESA_ID == 1 && (userId === '877' || userId === 877)) {
        return this.types.filter(type => type.serviceId !== 31 && type.serviceId !== 32)
      }

      return this.types.filter(type =>
        type.serviceId !== 30 &&
        type.serviceId !== 31 &&
        type.serviceId !== 32 &&
        type.serviceId !== 33 &&
        type.serviceId !== 34,
      )
    },

    filteredAutomations() {
      const items = this.automations || []

      return items.filter(item => {
        const byName = this.filterName
          ? String(item.name).toLowerCase().includes(String(this.filterName).toLowerCase())
          : true

        const byAdvancedName = this.advancedFilter?.name
          ? String(item.name).toLowerCase().includes(String(this.advancedFilter.name).toLowerCase())
          : true

        const byId = this.advancedFilter?.id
          ? String(item.id) === String(this.advancedFilter.id)
          : true

        const byStatus = this.advancedFilter &&
          this.advancedFilter.status !== null &&
          this.advancedFilter.status !== undefined
          ? Number(item.status) === Number(this.advancedFilter.status)
          : true

        return byName && byAdvancedName && byId && byStatus
      })
    },

    listItems() {
      const channelType = this.createAutomationForm?.channelType || 'sms'

      return (this.lists || [])
        .filter(list => (list.channelType || 'sms') === channelType)
        .map(list => ({
          label: list.name,
          value: list.id,
        }))
    },
  },

  async created() {
    await Promise.all([
      this.fetchListOptions(),
      this.fetchAutomations(),
      this.fetchSmsTemplates(),
      this.fetchRcsTemplates(),
    ])
  },

  methods: {
    isRcsAction(typeSms) {
      return Number(typeSms) === RCS_SMS_TYPE || Number(typeSms) === 31
    },

    getActionTypeLabel(typeSms) {
      return this.isRcsAction(typeSms) ? 'Mensagem de RCS' : 'Mensagem de SMS'
    },

    getActionChipColor(typeSms) {
      return this.isRcsAction(typeSms) ? 'success' : 'info'
    },

    resolveTemplateName(templateId, typeSms) {
      const templates = this.isRcsAction(typeSms)
        ? (this.allRcsTemplates || [])
        : (this.allTemplates || [])
      const template = templates.find(t => String(t.id) === String(templateId))

      return template?.name || `Template #${templateId}`
    },

    findRcsSupplierFromTemplate(template) {
      if (!template?.brokerCode || !template?.customerCode) return null

      return this.rcsSuppliers.find(s =>
        s.value?.broker_code === template.brokerCode &&
        s.value?.customer_code === template.customerCode &&
        (!template.supplierType || s.value?.type === template.supplierType),
      )?.value || null
    },
    ...mapActions('serviceListAutomation', ['find']),
    ...mapActions('serviceAutomations', {
      findAutomations: 'find',
      createAutomation: 'create',
      updateAutomation: 'update',
      removeAutomation: 'remove',
    }),
    ...mapActions('serviceAutomationActions', {
      findActions: 'find',
      createAction: 'create',
      updateAction: 'update',
      removeAction: 'remove',
    }),
    ...mapActions('smsTemplates', {
      getTemplates: 'find',
      createTemplate: 'create',
    }),

    openFixedTemplateDialog() {
      this.fixedTemplateForm = {
        name: null,
        message: '',
      }
      this.fixedTemplateNewVariable = ''
      this.dialogCreateFixedTemplate = true
    },

    closeFixedTemplateDialog() {
      this.dialogCreateFixedTemplate = false
      this.fixedTemplateForm = {
        name: null,
        message: '',
      }
      this.fixedTemplateNewVariable = ''
    },

    onVariableModeChanged() {
      this.actionForm.smsTemplateId = null
    },

    addFixedVariable() {
      const next = String(this.fixedTemplateNewVariable || '').trim().toUpperCase()
      if (!next) return
      if (next.includes('{') || next.includes('}')) {
        this.$notify?.('Informe apenas o nome da variável (sem chaves).', 'warning')
        return
      }
      this.fixedVariablesCustom = Array.from(new Set([...(this.fixedVariablesCustom || []), next]))
      this.fixedTemplateNewVariable = ''
    },

    insertFixedVariable(v) {
      const token = `{${String(v).toUpperCase()}}`
      this.fixedTemplateForm.message = `${this.fixedTemplateForm.message || ''}${token}`
    },

    getVariables(text) {
      const regex = /\{([^}]+)\}/g
      return String(text).match(regex)?.map(v => v.slice(1, -1).toUpperCase()) || []
    },

    fixTemplateContent(text) {
      const regex = /\{([^}]+)\}/g
      return String(text).replace(regex, (_, p1) => `{${p1.toUpperCase()}}`)
    },

    /** Mesma regra de contagem que `smsTemplates/index.vue` (fetchAction). */
    getMessageLenth(value) {
      return String(value.replace(/(?:\r\n|\r|\n)/g, '\n\n')).length
    },

    async submitFixedTemplate() {
      const rawMessage = this.fixedTemplateForm?.message || ''
      const fixedMessage = this.fixTemplateContent(rawMessage)
      const usedVariables = [...new Set(this.getVariables(fixedMessage))]

      const allowed = new Set(this.fixedVariablesAll || [])
      const invalid = usedVariables.filter(v => !allowed.has(v))

      if (invalid.length > 0) {
        this.$notify?.(
          `A mensagem contém variáveis não permitidas: ${invalid.map(v => `{${v}}`).join(', ')}`,
          'error',
        )
        return
      }

      const message = this.$utils.replaceSpecialCharacters(fixedMessage)

      if (this.getMessageLenth(message) > 160) {
        this.$notify?.('O campo mensagem pode conter até 160 caracteres.', 'error')
        return
      }

      // Mesmo payload que `smsTemplates/index.vue` → fetchAction (create), alinhado ao CreateSMSTemplateRequest / command.
      const body = {
        name: this.fixedTemplateForm.name,
        message,
        status: null,
        type: Number([500].includes(Number(this.actionForm.typeSms))
          ? smsTemplateType.Bet
          : smsTemplateType.Default),
        variables: usedVariables,
        useVoiceOtp: false,
      }

      try {
        const res = await this.createTemplate(body)
        const createdId = res?.data?.id ?? res?.data?.smsTemplate?.id ?? null

        if (createdId == null) {
          this.$notify?.('Erro ao cadastrar template.', 'error')
          return
        }

        await this.fetchSmsTemplates()
        this.dialogCreateFixedTemplate = false
        this.actionForm.smsTemplateId = createdId
        this.$notify?.('Template cadastrado com sucesso.', 'success')
      } catch (err) {
        console.error('Erro ao cadastrar template fixo:', err)
        this.$notify?.('Erro ao cadastrar template.', 'error')
      }
    },

    async fetchListOptions() {
      try {
        await this.find({ status: 1 })
      } catch (e) {
        // silenciosamente mantém lista vazia em caso de erro
      }
    },

    async fetchSmsTemplates() {
      try {
        const templates = await this.getTemplates({
          status: smsTemplateEnum.Approved,
          isRcs: 0,
        })

        this.allTemplates = templates || []
        this.filterActionTemplates()
      } catch (err) {
        console.error('Erro ao carregar templates de SMS:', err)
        this.allTemplates = []
        this.actionTemplates = []
      }
    },

    async fetchRcsTemplates() {
      try {
        const templates = await this.getTemplates({
          status: smsTemplateEnum.Approved,
          isRcs: 1,
        })

        this.allRcsTemplates = templates || []
      } catch (err) {
        console.error('Erro ao carregar templates RCS:', err)
        this.allRcsTemplates = []
        this.rcsActionTemplates = []
      }
    },

    filterRcsActionTemplates() {
      const supplier = this.rcsActionForm.supplier
      const rcsServiceId = smsTypes.find(t => t.value === RCS_SMS_TYPE)?.serviceId ?? 31

      if (!supplier?.broker_code) {
        this.rcsActionTemplates = []
        this.rcsActionForm.smsTemplateId = null
        return
      }

      this.rcsActionTemplates = (this.allRcsTemplates || []).filter(t => {
        const matchesService = Array.isArray(t.serviceIds) && t.serviceIds.includes(rcsServiceId)
        const matchesBroker = t.brokerCode === supplier.broker_code
        const matchesCustomer = t.customerCode === supplier.customer_code
        const matchesSupplierType = !supplier.type || !t.supplierType || t.supplierType === supplier.type

        return matchesService && matchesBroker && matchesCustomer && matchesSupplierType
      })

      if (
        this.rcsActionForm.smsTemplateId &&
        !this.rcsActionTemplates.some(t => String(t.id) === String(this.rcsActionForm.smsTemplateId))
      ) {
        this.rcsActionForm.smsTemplateId = null
      }
    },

    async fetchAutomations(params = {}) {
      const filter = {
        name: this.filterName || null,
        ...params,
      }

      await this.findAutomations(filter)
    },

    applyFilter() {
      this.fetchAutomations()
    },

    clearFilter() {
      this.filterName = null
      this.advancedFilter = {}
      this.fetchAutomations()
    },

    onAdvancedFilter(filter) {
      this.advancedFilter = filter || {}
      this.dialogFilter = false
      const params = {}

      if (this.advancedFilter.name) params.name = this.advancedFilter.name
      if (this.advancedFilter.id) params.id = this.advancedFilter.id
      if (this.advancedFilter.status !== null && this.advancedFilter.status !== undefined) {
        params.isActive = Number(this.advancedFilter.status) === 1
      }

      this.fetchAutomations(params)
    },

    onAutomationChannelTypeChanged() {
      if (this.isEditMode) return

      const selectedList = (this.lists || []).find(
        list => String(list.id) === String(this.createAutomationForm.listId),
      )

      if (
        this.createAutomationForm.listId &&
        (selectedList?.channelType || 'sms') !== this.createAutomationForm.channelType
      ) {
        this.createAutomationForm.listId = null
      }
    },

    openFormToCreate() {
      this.isEditMode = false
      this.editingAutomationId = null
      this.createAutomationForm = {
        name: null,
        listId: null,
        channelType: 'sms',
        isActive: true,
        sendMode: 'new',
        trigger: null,
        description: null,
      }
      this.sequences = []
      this.dialogCreateAutomation = true
    },

    openActionForm(item = null) {
      if (this.createAutomationForm.channelType === 'rcs') {
        this.openRcsActionForm(item)
        return
      }

      this.openSmsActionForm(item)
    },

    openSmsActionForm(item = null) {
      const userId = localStorage.getItem('id')
      const initialType = (process.env.VUE_APP_EMPRESA_ID == 1 && (userId === '943' || userId === 943))
        ? smsTypes.find(type => type.serviceId === 8)?.value || smsTypes[0].value
        : smsTypes[0].value

      if (item && item.id) {
        this.editingActionId = item.id
        this.actionForm = {
          name: item.name || null,
          typeSms: item.typeSms ?? initialType,
          smsTemplateId: item.smsTemplateId ?? null,
          variableMode: 'dynamic',
          delayAmount: item.delayAmount ?? null,
          delayUnit: item.delayUnit || 'minutes',
        }
      } else {
        this.editingActionId = null
        this.actionForm = {
          name: null,
          typeSms: initialType,
          smsTemplateId: null,
          variableMode: 'dynamic',
          delayAmount: null,
          delayUnit: 'minutes',
        }
      }
      this.filterActionTemplates()
      this.dialogActionRcs = false
      this.dialogAction = true
    },

    openRcsActionForm(item = null) {
      if (item && item.id) {
        this.editingActionId = item.id
        const template = (this.allRcsTemplates || []).find(
          t => String(t.id) === String(item.smsTemplateId),
        )

        this.rcsActionForm = {
          name: item.name || null,
          supplier: this.findRcsSupplierFromTemplate(template),
          smsTemplateId: item.smsTemplateId ?? null,
          delayAmount: item.delayAmount ?? null,
          delayUnit: item.delayUnit || 'minutes',
        }
      } else {
        this.editingActionId = null
        this.rcsActionForm = {
          name: null,
          supplier: this.rcsSuppliers[0]?.value || null,
          smsTemplateId: null,
          delayAmount: null,
          delayUnit: 'minutes',
        }
      }

      this.filterRcsActionTemplates()
      this.dialogAction = false
      this.dialogActionRcs = true
    },

    closeCreateAutomationDialog() {
      this.dialogCreateAutomation = false
      this.isEditMode = false
      this.editingAutomationId = null
      this.sequences = []
      this.createAutomationForm = {
        name: null,
        listId: null,
        channelType: 'sms',
        isActive: true,
        sendMode: 'new',
        trigger: null,
        description: null,
      }
    },

    async submitCreateAutomation() {
      if (!this.createAutomationForm.name || !this.createAutomationForm.channelType) return

      const body = {
        name: this.createAutomationForm.name,
        listId: this.createAutomationForm.listId,
        channelType: this.createAutomationForm.channelType,
        sendMode: this.createAutomationForm.sendMode,
        isActive: !!this.createAutomationForm.isActive,
        trigger: this.createAutomationForm.trigger || null,
        description: this.createAutomationForm.description || null,
      }

      try {
        if (this.isEditMode && this.editingAutomationId !== null) {
          const status = await this.updateAutomation({ id: this.editingAutomationId, body })

          if (typeof status === 'number' && status >= 200 && status < 300) {
            this.$notify?.('Automação atualizada com sucesso', 'success')
          }
        } else {
          const status = await this.createAutomation(body)

          if (typeof status === 'number' && status >= 200 && status < 300) {
            this.$notify?.('Automação criada com sucesso', 'success')
          }
        }

        await this.fetchAutomations()
        this.closeCreateAutomationDialog()
      } catch (err) {
        this.$notify?.(
          this.isEditMode ? 'Erro ao atualizar automação' : 'Erro ao criar automação',
          'error',
        )
      }
    },

    async openFormToEdit(item) {
      this.isEditMode = true
      this.editingAutomationId = item.id
      this.createAutomationForm = {
        name: item.name || null,
        listId: item.listId || null,
        channelType: item.channelType || 'sms',
        isActive: (item.status ?? 1) === 1,
        sendMode: item.sendMode || 'new',
        trigger: item.trigger || null,
        description: item.description || null,
      }
      this.dialogCreateAutomation = true
      await Promise.all([
        this.fetchSmsTemplates(),
        this.fetchRcsTemplates(),
      ])
      await this.loadActions(item.id)

      if (!item.channelType && this.sequences.length > 0) {
        const inferredType = this.isRcsAction(this.sequences[0].typeSms) ? 'rcs' : 'sms'
        this.createAutomationForm.channelType = inferredType
      }
    },

    confirmDelete(item) {
      if (confirm(`Deseja realmente excluir a automação "${item.name}"?`)) {
        this.deleteAutomation(item)
      }
    },

    async deleteAutomation(item) {
      try {
        const status = await this.removeAutomation(item.id)

        if (typeof status === 'number' && status >= 200 && status < 300) {
          this.$notify?.('Automação excluída com sucesso', 'success')
          await this.fetchAutomations()
        }
      } catch (err) {
        this.$notify?.('Erro ao excluir automação', 'error')
      }
    },

    filterActionTemplates() {
      const smsType = smsTypes.find(t => t.value === this.actionForm.typeSms)

      if (!smsType) {
        this.actionTemplates = []
        return
      }

      this.actionTemplates = (this.allTemplates || []).filter(t =>
        Array.isArray(t.serviceIds) && t.serviceIds.includes(smsType.serviceId),
      )
    },

    async loadActions(automationId) {
      try {
        const actions = await this.findActions({
          automationId,
          params: { isActive: true },
        })

        this.sequences = (actions || []).map(action => ({
          id: action.id,
          enabled: !!action.isActive,
          name: action.name,
          typeSms: action.typeSms,
          smsTemplateId: action.smsTemplateId,
          delayAmount: action.delayAmount,
          delayUnit: action.delayUnit,
          typeLabel: this.getActionTypeLabel(action.typeSms),
          delayLabel: action.delayUnit === 'hours'
            ? `Após ${action.delayAmount} hora(s)`
            : `Após ${action.delayAmount} minuto(s)`,
          message: this.resolveTemplateName(action.smsTemplateId, action.typeSms),
        }))
      } catch (err) {
        console.error('Erro ao carregar ações da automação:', err)
        this.sequences = []
      }
    },

    async toggleActionEnabled(item) {
      if (!item?.id) return

      try {
        const status = await this.updateAction({
          id: item.id,
          body: { isActive: !!item.enabled },
        })

        if (typeof status === 'number' && status >= 200 && status < 300) {
          this.$notify?.('Ação atualizada com sucesso', 'success')
        } else {
          this.$notify?.('Erro ao atualizar ação', 'error')
        }
      } catch (err) {
        this.$notify?.('Erro ao atualizar ação', 'error')
      } finally {
        if (this.editingAutomationId) {
          await this.loadActions(this.editingAutomationId)
        }
      }
    },

    confirmDeleteAction(item) {
      if (!item?.id) return
      if (confirm('Deseja realmente excluir esta ação?')) {
        this.deleteAction(item)
      }
    },

    async deleteAction(item) {
      try {
        const status = await this.removeAction(item.id)
        if (typeof status === 'number' && status >= 200 && status < 300) {
          this.$notify?.('Ação excluída com sucesso', 'success')
          if (this.editingAutomationId) {
            await this.loadActions(this.editingAutomationId)
          }
        } else {
          this.$notify?.('Erro ao excluir ação', 'error')
        }
      } catch (err) {
        this.$notify?.('Erro ao excluir ação', 'error')
      }
    },

    async submitAction() {
      if (!this.editingAutomationId) {
        this.$notify?.('Automação não selecionada para vincular a ação.', 'error')
        return
      }

      if (!this.actionForm.name || !this.actionForm.smsTemplateId || !this.actionForm.delayAmount) {
        return
      }

      const body = {
        name: this.actionForm.name,
        typeSms: this.actionForm.typeSms,
        smsTemplateId: this.actionForm.smsTemplateId,
        delayAmount: Number(this.actionForm.delayAmount),
        delayUnit: this.actionForm.delayUnit,
        isActive: true,
      }

      try {
        const status = this.editingActionId
          ? await this.updateAction({ id: this.editingActionId, body })
          : await this.createAction({ automationId: this.editingAutomationId, body })

        if (typeof status === 'number' && status >= 200 && status < 300) {
          this.$notify?.(
            this.editingActionId ? 'Ação atualizada com sucesso' : 'Ação criada com sucesso',
            'success',
          )
          await this.loadActions(this.editingAutomationId)
          this.dialogAction = false
          this.editingActionId = null
        }
      } catch (err) {
        console.error('Erro ao salvar ação da automação:', err)
        this.$notify?.('Erro ao salvar ação da automação', 'error')
      }
    },

    async submitRcsAction() {
      if (!this.editingAutomationId) {
        this.$notify?.('Automação não selecionada para vincular a ação.', 'error')
        return
      }

      if (
        !this.rcsActionForm.name ||
        !this.rcsActionForm.supplier ||
        !this.rcsActionForm.smsTemplateId ||
        !this.rcsActionForm.delayAmount
      ) {
        return
      }

      const body = {
        name: this.rcsActionForm.name,
        typeSms: RCS_SMS_TYPE,
        smsTemplateId: this.rcsActionForm.smsTemplateId,
        delayAmount: Number(this.rcsActionForm.delayAmount),
        delayUnit: this.rcsActionForm.delayUnit,
        isActive: true,
      }

      try {
        const status = this.editingActionId
          ? await this.updateAction({ id: this.editingActionId, body })
          : await this.createAction({ automationId: this.editingAutomationId, body })

        if (typeof status === 'number' && status >= 200 && status < 300) {
          this.$notify?.(
            this.editingActionId ? 'Ação RCS atualizada com sucesso' : 'Ação RCS criada com sucesso',
            'success',
          )
          await this.loadActions(this.editingAutomationId)
          this.dialogActionRcs = false
          this.editingActionId = null
        }
      } catch (err) {
        console.error('Erro ao salvar ação RCS da automação:', err)
        this.$notify?.('Erro ao salvar ação RCS da automação', 'error')
      }
    },
  },
}
</script>

